import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AuthModal } from "../../control/components/AuthModal";
import { ConfirmDialog } from "../../control/components/ConfirmDialog";
import { track } from "../../analytics";
import { IconChevron } from "../../control/ui/icons";
import { userApi } from "../../lib/api";
import { useIsPortrait } from "../../lib/useIsPortrait";
import { RoomChat } from "./RoomChat";
import { RoomRoster } from "./RoomRoster";
import type { RoomCtx } from "./useRoom";

/** Whether this browser last left the chat folded — a convenience, blocked storage reads as open. */
const CHAT_KEY = "gp:room:chat";
function readChatOpen(): boolean {
  try {
    return localStorage.getItem(CHAT_KEY) !== "closed";
  } catch {
    return true;
  }
}
function writeChatOpen(open: boolean): void {
  try {
    localStorage.setItem(CHAT_KEY, open ? "open" : "closed");
  } catch {
    /* it will open next visit; harmless */
  }
}

/**
 * THE GOON ROOM's frame around the screen's glass: the roster over its top-left corner,
 * the chat beside it — to the right on a wide screen, below on a phone — and the room's
 * cards (sign in, closed, thrown out). The glass itself is DisplayApp's, handed in as
 * `children`: a room IS the host's screen, mirrored, and nothing here draws a tile.
 *
 * Membership is the room socket's (useRoom, opened by DisplayApp); this only draws what
 * it reports. Signed out, the door is a sign-in card: a room is people, and a person
 * needs a name. Closed or thrown out, the card says so over black — the display socket
 * keeps resolving the room's stub, so nothing else tears down.
 */
export function RoomFrame({ room, signedIn, children }: { room: RoomCtx | null; signedIn: boolean; children: React.ReactNode }) {
  const portrait = useIsPortrait();
  // Signed out, the room still plays and the chat still moves (veiled): the sign-in door
  // is the chat's box and the crown, not a card over the glass.
  const [authOpen, setAuthOpen] = useState(false);
  // THE CHAT FOLDS (owner, 2026-09-19): one slim tab — a rail beside the wall, a bar
  // under it on a phone — is the control in both states; the chevron turns to say which
  // way it goes. What arrived while it was folded counts as unread.
  const [chatOpenPref, setChatOpen] = useState(readChatOpen);
  // A house room with its chat off has no chat to fold: the glass takes the whole frame.
  const hasChat = !room?.chatOff;
  const chatOpen = chatOpenPref && hasChat;
  const seenRef = useRef(0);
  const total = room?.messages.length ?? 0;
  useEffect(() => {
    if (chatOpen) seenRef.current = total;
  }, [chatOpen, total]);
  const unread = chatOpen ? 0 : Math.max(0, total - seenRef.current);
  const toggleChat = (open: boolean) => {
    setChatOpen(open);
    writeChatOpen(open);
    track("room_chat_folded", { open });
  };

  const isOwner = room?.role === "owner";
  const chatLive = room?.status === "open";

  // THE HOST LEAVING ENDS THE ROOM (owner, 2026-09-19). The server's grace exists for a
  // reload and a flaky link; a host who walks off to another page has left, and the
  // members should not sit in front of a dead board for thirty seconds. In-page links
  // are caught before the router sees them and confirmed — "since you are the host,
  // leaving ends the room" — and the room is closed before the page turns. A leave that
  // got past the confirm (the browser's Back) still ends it: this frame unmounts with
  // the address already elsewhere. A reload never unmounts, so the grace still covers
  // it, and a re-point keeps this frame mounted, so it never closes the room. A link
  // that opens a new tab (a profile, the editor) is not a leave.
  const navigate = useNavigate();
  const roomId = room?.roomId ?? null;
  const [leaveTo, setLeaveTo] = useState<string | null>(null);
  const closedRef = useRef(false);
  useEffect(() => {
    if (!isOwner || !roomId) return;
    const onClick = (e: MouseEvent) => {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const a = (e.target as Element | null)?.closest?.("a[href]") as HTMLAnchorElement | null;
      if (!a || a.target === "_blank" || a.origin !== window.location.origin || a.pathname === window.location.pathname) return;
      e.preventDefault();
      e.stopPropagation();
      setLeaveTo(a.pathname + a.search + a.hash);
    };
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, [isOwner, roomId]);
  useEffect(() => {
    if (!isOwner || !roomId) return;
    closedRef.current = false;
    return () => {
      if (!closedRef.current && !window.location.pathname.startsWith("/room/")) void userApi.roomClose(roomId).catch(() => undefined);
    };
  }, [isOwner, roomId]);
  const stay = () => {
    setLeaveTo(null);
    track("room_leave_prompted", { choice: "stay" });
  };
  const leave = () => {
    const dest = leaveTo;
    if (!dest || !roomId) return;
    setLeaveTo(null);
    track("room_leave_prompted", { choice: "leave" });
    closedRef.current = true;
    void userApi
      .roomClose(roomId)
      .catch(() => undefined)
      .then(() => navigate(dest));
  };

  const foldTab = (
    <button
      type="button"
      onClick={() => toggleChat(!chatOpen)}
      aria-label={chatOpen ? "Hide the chat" : unread ? `Show the chat, ${unread} new` : "Show the chat"}
      title={chatOpen ? "Hide the chat" : "Show the chat"}
      className={
        portrait
          ? "flex h-10 w-full shrink-0 items-center justify-center gap-2 border-t border-[var(--color-edge)] bg-[var(--color-panel)] text-xs text-zinc-300"
          : "flex w-10 shrink-0 flex-col items-center justify-center gap-2 border-l border-[var(--color-edge)] bg-[var(--color-panel)] text-xs text-zinc-300"
      }
    >
      <IconChevron width={16} height={16} className={portrait ? (chatOpen ? "" : "rotate-180") : chatOpen ? "-rotate-90" : "rotate-90"} />
      <span className={portrait ? "" : "[writing-mode:vertical-rl]"}>Chat</span>
      {!chatOpen && unread ? (
        <span className="rounded-full bg-[var(--color-accent)] px-1.5 py-0.5 text-[10px] font-bold leading-none text-black">{unread > 99 ? "99+" : unread}</span>
      ) : null}
    </button>
  );

  return (
    <div className={portrait ? "flex min-h-0 flex-1 flex-col" : "flex min-h-0 flex-1"}>
      <div
        className={
          // Portrait: the glass is a 16:9 band over the chat; with the chat folded the band
          // grows to fill the column, so folding the chat never leaves the space empty.
          portrait
            ? chatOpen
              ? "relative flex aspect-video w-full shrink-0 bg-black"
              : "relative flex min-h-0 w-full flex-1 bg-black"
            : "relative flex min-w-0 flex-1 bg-black"
        }
      >
        {children}
        {room ? (
          <RoomRoster
            members={room.members}
            watching={room.watching}
            you={room.you}
            isOwner={isOwner}
            onMod={room.mod}
            after={
              // Nobody hosts a house room and its chat can be off, so the way out sits on
              // the glass: here, because a toast lands in the top-right corner and covers it.
              room.house ? (
                <Link to="/rooms" className="whitespace-nowrap rounded-full border border-white/10 bg-black/60 px-3 py-1.5 text-xs font-medium text-zinc-100 shadow-lg backdrop-blur hover:bg-black/80">
                  Leave
                </Link>
              ) : null
            }
          />
        ) : null}
        {room && chatLive && room.showing === null ? (
          <Notice>{isOwner ? "Pick something to play from the bar — everyone here will see it." : "Waiting for the host to pick something."}</Notice>
        ) : null}
        {room?.status === "closed" || room?.kickedReason === "ended" ? (
          <Notice>{room?.house ? "This room has closed." : "The host left — this room has closed."}</Notice>
        ) : room?.status === "kicked" ? (
          <Notice>{room.kickedReason === "banned" ? "You were banned from this room." : "You were removed from this room."}</Notice>
        ) : room?.status === "refused" ? (
          <Notice>{room.refusal?.message ?? "You can't join this room."}</Notice>
        ) : null}

      </div>
      {hasChat ? foldTab : null}
      {chatOpen ? (
        <div className={portrait ? "min-h-0 flex-1 border-t border-[var(--color-edge)]" : "w-80 shrink-0 border-l border-[var(--color-edge)]"}>
          <RoomChat
            roomId={room?.roomId ?? ""}
            name={room?.name ?? "Goon Room"}
            isPublic={room?.isPublic ?? false}
            watching={room?.watching ?? 0}
            messages={room?.messages ?? []}
            notes={room?.notes ?? []}
            activity={room?.activity ?? []}
            onSend={(body) => room?.sendChat(body)}
            portrait={portrait}
            disabled={!chatLive}
            error={room?.frameError ?? null}
            isOwner={isOwner}
            you={room?.you ?? null}
            members={room?.members ?? []}
            onMod={(action, username) => room?.mod(action, username)}
            signedIn={signedIn}
            onSignIn={() => setAuthOpen(true)}
          />
        </div>
      ) : null}
      <ConfirmDialog
        open={leaveTo !== null}
        title="Leave the room?"
        message="You're the host — leaving ends the room for everyone in it."
        confirmLabel="Leave and end it"
        onConfirm={leave}
        onCancel={stay}
      />
      {authOpen && !signedIn ? (
        <AuthModal initialMode="register" reason="Create a free account to join this room." onClose={() => setAuthOpen(false)} />
      ) : null}
    </div>
  );
}

/** A card over the glass: the room's word on why there is nothing to watch. */
function Notice({ children }: { children: React.ReactNode }) {
  return (
    <div className="absolute inset-0 z-20 flex items-center justify-center bg-black/70 p-6">
      <div className="flex max-w-md flex-col items-center gap-3 rounded-2xl border border-[var(--color-edge)] bg-[var(--color-panel)] px-6 py-5 text-center text-sm text-zinc-200 shadow-2xl">
        {children}
      </div>
    </div>
  );
}
